import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import type { ChangeDefaultBillingMethodFn } from '@/features/billing'
import { type JobStore } from '@/features/jobs/JobStore'
import { type DraftJobStore } from './DraftJobStore'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  merge,
  type Observable,
  type OperatorFunction,
  Subject,
  firstValueFrom,
} from 'rxjs'
import { action, computed, observable } from 'mobx'
import { Job } from '@/features/jobs/Job'
import type { AnswersMap } from '@/features/jobs/questions/questions'
import {
  batchElements,
  every,
  filterNonNil,
  flowToggle,
  fromAbortSignal,
  shareBehavior,
  unpair,
} from '@/utils/rx-util'
import {
  concatMap,
  distinctUntilChanged,
  filter,
  find,
  map,
  mergeAll,
  share,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { fromStream, toStream } from '@/utils/mobx-util'
import { Id } from '@/infra/id'
import type { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import { extractMessageFromError } from '@/utils/error-util'

/**
 * Possible draft change types.
 */
export type DraftChange =
  | WorkspaceSelected
  | OwnerSelected
  | SupportingClientSelected
  | TeamSelected
  | LegendSelected
  | AnswersChanged
  | Renamed
  | RequestedProviderChanged
  | CouponSelected
  | CouponCleared
  | BillingMethodSelected

/**
 * Workspace was selected.
 */
export interface WorkspaceSelected {
  type: 'WorkspaceSelected'
  workspace: Workspace | null
}

/**
 * Symbol representing an explicit selection of "myself".
 */
const TEAM_MYSELF = Symbol('Myself')

/**
 * Team selection options.
 */
export type TeamSelection = Team | typeof TEAM_MYSELF
export const TeamSelection = {
  /**
   * Explicitly no team; want to create job for myself.
   */
  myself: TEAM_MYSELF,
} as const

/**
 * Owner was selected.
 */
export interface OwnerSelected {
  type: 'OwnerSelected'
  owner: Member | null
}

/**
 * Supporting client was selected.
 */
export type SupportingClientSelected =
  SupportingClientAdded | SupportingClientRemoved

/**
 * Supporting client was added.
 */
export interface SupportingClientAdded {
  type: 'SupportingClientAdded'
  member: Member
}

/**
 * Supporting client was removed.
 */
export interface SupportingClientRemoved {
  type: 'SupportingClientRemoved'
  member: Member
}

/**
 * Team was selected.
 */
export interface TeamSelected {
  type: 'TeamSelected'
  team: TeamSelection | null
}

/**
 * Legend was selected.
 */
export interface LegendSelected {
  type: 'LegendSelected'
  legend: PublishedLegend | null
}

/**
 * Answers were changed.
 */
export interface AnswersChanged {
  type: 'AnswersChanged'
  /**
   * The answers to save, `null` when there are none that can be saved.
   */
  answers: AnswersMap | null
}

/**
 * Job was renamed.
 */
export interface Renamed {
  type: 'Renamed'
  newName: string
}

/**
 * Explicitly chose "first available".
 */
const REQUESTED_PROVIDER_FIRST_AVAILABLE = Symbol('First Available')

/**
 * Requested provider selection.
 */
export type RequestedProviderSelection =
  Member | typeof REQUESTED_PROVIDER_FIRST_AVAILABLE
export const RequestedProviderSelection = {
  /**
   * First available provider.
   */
  firstAvailable: REQUESTED_PROVIDER_FIRST_AVAILABLE,
} as const

/**
 * The requested provider was changed.
 */
export interface RequestedProviderChanged {
  type: 'RequestedProviderChanged'
  requestedProvider: RequestedProviderSelection | null
}

/**
 * A Coupon was selected.
 */
export interface CouponSelected {
  type: 'CouponSelected'
  coupon: Coupon
}

/**
 * The coupon was cleared.
 */
export interface CouponCleared {
  type: 'CouponCleared'
}

/**
 * A billing method was selected.
 */
export interface BillingMethodSelected {
  type: 'BillingMethodSelected'
  billingMethod: BillingMethod
}

/**
 * Persister creates the job when we have all the necessary input
 * and updates it as things change, ensuring updates are serialized.
 *
 * NOTE: The `persist$` pipeline is subscribed to immediately since it is being
 * used with `fromStream`.
 */
export class DraftPersister {
  /**
   * Used by `queue`.
   *
   * @private
   */
  private readonly _manuallyQueuedChanges = new Subject<DraftChange>()

  /**
   * Used to signal to the UI that the loaded Job was not a Draft.
   *
   * @private
   */
  private readonly _notADraft = new Subject<Job>()

  /**
   * Controls whether the Persister has started.
   * Until this emits `true` for the first time, changes
   * are buffered.
   *
   * @private
   */
  private readonly _running = new BehaviorSubject(false)

  /**
   * Sequenced PersistInputs.
   * @private
   */
  private readonly _persistInput$

  /**
   * The persist pipeline.
   */
  readonly persist$

  /**
   * Used for serializing persists and correlating persist
   * requests with responses. For example, if we queue 3 persists within
   * a short timeframe, we want all 3 to resolve once the last request finishes.
   *
   * @private
   */
  private readonly _inputSeq$

  /**
   * Observable that emits a `boolean` for whether or not we are currently
   * persisting.
   */
  readonly persisting$

  /**
   * Emits a value when a Job is created.
   */
  readonly jobCreated$

  /**
   * Emits a value if we detect that the loaded Job is not a Draft.
   * Used by the View to redirect to Job Details.
   */
  readonly notADraft$

  /**
   * Emits changes that were persisted.
   */
  readonly persistedChanges$

  /**
   * The Job that is being drafted.
   */
  @observable accessor job = Job.none

  /**
   * The Job being drafted as a stream. Emits
   * immediately on subscribe, but ONLY if we have an actual
   * Job instance.
   */
  readonly job$ = toStream(() => this.job, true).pipe(
    filter((job) => job !== Job.none),
  )

  /**
   * The last persist result.
   */
  readonly lastPersistResult$

  /**
   * Streams as MobX Observables.
   *
   * @private
   */
  private readonly _$

  /**
   * Constructor.
   *
   * @param changes$ Observable that emits changes to be persisted.
   *  These get sequenced through the `sequenceAndBatchPersistOperations`
   *  operator which can then batch and serialize them as it sees fit.
   */
  constructor(
    private readonly _changes$: Observable<DraftChange>,
    private readonly _jobStore: JobStore,
    private readonly _draftJobStore: DraftJobStore,
    private readonly _changeDefaultBillingMethod: ChangeDefaultBillingMethodFn,
  ) {
    this._persistInput$ = merge(
      this._changes$,
      this._manuallyQueuedChanges,
    ).pipe(
      map((change, i): PersistInput => [i + 1, this.job, change]),
      share(),
    )

    this.persist$ = this._persistInput$.pipe(
      sequenceAndBatchPersistOperations(
        (changes) => this._createDraft(changes),
        (job, changes) => this._persistChanges(job, changes),
        this._running,
      ),
      share(),
    )

    this._inputSeq$ = this._persistInput$.pipe(unpair(), shareBehavior(0))

    this.persisting$ = combineLatest([
      this._inputSeq$,
      this.persist$.pipe(
        map((x) => x.seq),
        shareBehavior(0),
      ),
    ]).pipe(
      // We are persisting if the last persist stream emission
      // seq number is lower than the last input seq.
      map(([inputSeq, lastPersistSeq]) => lastPersistSeq < inputSeq),
      distinctUntilChanged(),
      shareBehavior(false),
    )

    this.jobCreated$ = this.persist$.pipe(
      // The Persist result only contains a `job` when one was just created.
      map((x) => x.job),
      filterNonNil(),
      // Set the job instance.
      tap((job) => this._set(job)),
      share(),
    )

    this.notADraft$ = this._notADraft.asObservable()

    this.persistedChanges$ = this.persist$.pipe(
      map((x) => x.persistedChanges),
      mergeAll(),
      share(),
    )

    this.lastPersistResult$ = this.persist$.pipe(
      shareBehavior(PersistResult.nothingHappened(0)),
    )

    this._$ = {
      persisting: fromStream(false, this.persisting$),
      lastPersistResult: fromStream(
        PersistResult.nothingHappened(0),
        this.lastPersistResult$,
      ),
    }

    this.queueChange = this.queueChange.bind(this)
    this.persist$.subscribe()
    this._changes$.subscribe()
    this.jobCreated$.subscribe()
    this.persisting$.subscribe()
  }

  /**
   * Whether or not we are currently persisting.
   */
  @computed
  get persisting() {
    return this._$.persisting()
  }

  /**
   * The result of the last persist.
   */
  @computed
  get lastPersistResult() {
    return this._$.lastPersistResult()
  }

  /**
   * The time that the draft was last saved.
   */
  @computed
  get lastSavedAt() {
    return this.job.dateModified
  }

  /**
   * Whether the job has been created or if we can still make foundational changes to it.
   */
  @computed
  get jobWasCreated() {
    return this.job !== Job.none
  }

  /**
   * Waits for queued persists to have flushed.
   * If no persists are queued, resolves immediately.
   *
   * @param signal If aborted, resolves immediately.
   */
  async waitForFlush(signal?: AbortSignal) {
    // Wait for the latest queued persist to have been completed.
    await firstValueFrom(
      this.persisting$.pipe(
        find((v) => !v),
        takeUntil(fromAbortSignal(signal)),
      ),
      { defaultValue: null },
    )
  }

  /**
   * Prepares the Persister.
   *
   * @param jobId
   * @param signal
   */
  async prepare(jobId: Id, signal?: AbortSignal) {
    if (!Id.some(jobId)) {
      return
    }

    const job =
      (await this._jobStore.fetchJob(jobId, undefined, signal)) || Job.none
    signal?.throwIfAborted()
    if (!job.isDraft) {
      this._notADraft.next(job)
    }
    this._set(job)
  }

  /**
   * Starts persisting; any buffered and future changes will start flowing.
   */
  start() {
    this._running.next(true)
  }

  /**
   * Queues a change and waits for the next flush.
   *
   * @param change
   * @param signal
   */
  async queueChange(
    change: DraftChange,
    signal?: AbortSignal,
  ): Promise<PersistResult> {
    this._manuallyQueuedChanges.next(change)
    await this.waitForFlush(signal)
    return this.lastPersistResult
  }

  /**
   * Sets the Job instance.
   *
   * @param job
   */
  @action.bound
  private _set(job: Job) {
    this.job = job
  }

  /**
   * Creates the draft.
   *
   * @returns The Job, or `null` if there are not enough inputs to create it
   *  yet, in which case the changes are buffered until more come through.
   */
  private async _createDraft(
    changes: readonly DraftChange[],
  ): Promise<Job | null> {
    const [params, otherChanges] = parseDraftParams(changes)
    if (!params) {
      return null
    }

    const job = params.teamId
      ? await this._draftJobStore.createTeamDraftJob({
          legendVersionId: params.legendVersionId,
          teamId: params.teamId,
          clientUserPublicId: params.ownerId,
        })
      : await this._draftJobStore.createDraftJob(
          params.legendVersionId,
          params.ownerId,
        )

    await this._persistChanges(job, otherChanges)
    return job
  }

  /**
   * Persists changes to the Job.
   *
   * @returns The changes that were persisted, a subset of `changes`.
   */
  private async _persistChanges(
    job: Job,
    changes: readonly DraftChange[],
  ): Promise<DraftChange[]> {
    const persistedChanges: DraftChange[] = []
    for (const change of changes) {
      switch (change.type) {
        case 'LegendSelected': {
          if (change.legend) {
            await this._draftJobStore.upgradeLegendVersion(job.id)
            persistedChanges.push(change)
          }
          break
        }
        case 'SupportingClientAdded': {
          await this._jobStore.addSupportingClientAsAdmin({
            jobId: job.id,
            clientUserPublicId: change.member.user.publicId,
            reason: 'Editing draft',
          })
          persistedChanges.push(change)
          break
        }
        case 'SupportingClientRemoved': {
          await this._jobStore.removeCollaboratorAsAdmin({
            jobId: job.id,
            collaboratorUserPublicId: change.member.user.publicId,
            reason: 'Editing draft',
          })
          persistedChanges.push(change)
          break
        }
        case 'AnswersChanged':
          if (change.answers) {
            await this._draftJobStore.replaceQuestionAnswers(
              job.id,
              change.answers,
            )
          }
          persistedChanges.push(change)

          break
        case 'Renamed':
          await this._jobStore.rename(job, change.newName)
          persistedChanges.push(change)
          break
        case 'RequestedProviderChanged':
          if (change.requestedProvider) {
            const requestedProId =
              change.requestedProvider ===
              RequestedProviderSelection.firstAvailable
                ? null
                : change.requestedProvider.user.publicId

            await this._draftJobStore.requestChampionProvider(
              job.id,
              requestedProId,
            )

            persistedChanges.push(change)
          }

          break
        case 'CouponSelected':
          await this._draftJobStore.applyCoupon(job.id, change.coupon.id)
          persistedChanges.push(change)
          break
        case 'CouponCleared':
          await this._draftJobStore.removeCoupon(job.id)
          persistedChanges.push(change)
          break
        case 'BillingMethodSelected':
          await this._changeDefaultBillingMethod(
            change.billingMethod,
            job.ownerTeam,
          )
          persistedChanges.push(change)
          break
        default:
          // Workspace and Team changes should not be possible
          // after having drafted the job.
          console.warn(`Unhandled change type: ${change.type}`, change)
      }
    }

    return persistedChanges
  }
}

/**
 * Operator used for sequencing and batching persist operations.
 *
 * @param createDraft Creates the draft, or resolves to `null` when there is
 *  not enough input yet.
 * @param updateDraft Persists changes to an existing draft.
 * @param running$ Controls whether we are persisting or buffering from
 *  the outside. Used to buffer until the persister has started. Should
 *  emit immediately on subscribe.
 */
function sequenceAndBatchPersistOperations(
  createDraft: (changes: readonly DraftChange[]) => Promise<Job | null>,
  updateDraft: (
    job: Job,
    changes: readonly DraftChange[],
  ) => Promise<DraftChange[]>,
  running$: Observable<boolean>,
): OperatorFunction<PersistInput, PersistResult> {
  // Operator that ensures persists are serialized
  // but also batched when possible.
  return (source$) =>
    defer(() => {
      // Used to control the flow.
      // Whenever we are processing a change, we stop the flow and start buffering.
      // When done, we start flowing and process the buffered inputs as a batch.
      const flowingSubject = new BehaviorSubject<boolean>(true)
      const flowing$ = every([running$, flowingSubject])
      // If drafting fails because we didn't have enough info to create
      // a draft, the changes are stashed here so we can include them
      // the next time changes flow through.
      // Example:
      //   1. [TeamSelected], not enough to create a draft on its own.
      //      ^ stashed
      //   2. [LegendSelected], combine with stash, now we
      //      have [TeamSelected, LegendSelected], that's enough
      //      and so the draft gets created and the stash is cleared.
      let stashedForNextRound: readonly DraftChange[] = []
      return source$.pipe(
        flowToggle(flowing$, true),
        // If there is a batch waiting to be flushed it will be caught
        // by `batchElements.`
        batchElements(10),
        // Once we have a batch to process,
        // stop the flow and start buffering.
        tap(() => flowingSubject.next(false)),
        // Process the batch.
        concatMap(async (batch) => {
          // Gets the job and seq from the last entry in the batch.
          const [seq, job] = batch[batch.length - 1]
          const optimized = optimizeChanges([
            ...stashedForNextRound,
            ...batch.map(([, , c]) => c),
          ])
          // Clear the stash.
          stashedForNextRound = []
          return job === Job.none
            ? // If there was no job, try to create it now.
              maybeCreateDraft(seq, optimized)
            : // Otherwise update the existing one
              persistChanges(job, seq, optimized)
        }),
        // After a batch has been processed,
        // start the flow again.
        tap(() => flowingSubject.next(true)),
      )

      /**
       * Persists a batch of changes to the job.
       *
       * @param job
       * @param seq
       * @param changes
       */
      async function persistChanges(
        job: Job,
        seq: number,
        changes: DraftChange[],
      ) {
        // Persist changes to the job.
        try {
          const persistedChanges = await updateDraft(job, changes)
          return PersistResult.draftUpdated(seq, persistedChanges)
        } catch (err) {
          return PersistResult.failed(seq, err)
        }
      }

      /**
       * Create a Draft if we have collected enough input to do so.
       *
       * @param seq
       * @param changes
       */
      async function maybeCreateDraft(
        seq: number,
        changes: readonly DraftChange[],
      ): Promise<PersistResult> {
        try {
          const job = await createDraft(changes)
          if (!job) {
            // Not enough info to create the draft yet; stash these changes
            // so we can include them for the next round.
            stashedForNextRound = changes
            return PersistResult.nothingHappened(seq)
          }
          return PersistResult.draftCreated(seq, job, changes)
        } catch (err) {
          return PersistResult.failed(seq, err)
        }
      }
    })
}

/**
 * Optimizes the changes by removing duplicates.
 *
 * @param changes
 */
function optimizeChanges(changes: DraftChange[]): DraftChange[] {
  const optimized: DraftChange[] = []
  // Reverse loop because we only want the last entry of each type.
  for (let i = changes.length - 1; i >= 0; i--) {
    const change = changes[i]
    if (optimized.some((c) => c.type === change.type)) {
      continue
    }
    optimized.unshift(change)
  }

  return optimized
}

/**
 * Delegate for queueing a change and waiting for it to have been persisted.
 */
export type QueueChange = (
  change: DraftChange,
  signal?: AbortSignal,
) => Promise<PersistResult>

/**
 * Input for the persist operator.
 */
export type PersistInput = [number, Job, DraftChange]

/**
 * Persist result.
 */
export interface PersistResult {
  /**
   * The sequence number matching the last change that was successfully persisted.
   */
  seq: number
  /**
   * If specified, sets the `Job` instance to this.
   * Used by the result of creating the Draft.
   */
  job?: Job
  /**
   * If specified, the operation failed.
   */
  errorMessage?: string
  /**
   * The changes that were persisted, if any.
   */
  persistedChanges: readonly DraftChange[]
}

/**
 * Creation function for a `PersistResult` shape.
 *
 * @param seq
 * @param job
 * @param errorMessage
 * @param persistedChanges
 */
export function PersistResult(
  seq: number,
  job?: Job,
  errorMessage?: string,
  persistedChanges: readonly DraftChange[] = [],
): PersistResult {
  return { seq, job, errorMessage, persistedChanges }
}

/**
 * Nothing happened.
 *
 * @param seq
 */
PersistResult.nothingHappened = function nothingHappened(seq: number) {
  return PersistResult(seq)
}

/**
 * Job draft was created.
 *
 * @param seq
 * @param job
 * @param persistedChanges
 */
PersistResult.draftCreated = function draftCreated(
  seq: number,
  job: Job,
  persistedChanges: readonly DraftChange[],
) {
  return PersistResult(seq, job, undefined, persistedChanges)
}

/**
 * Job was updated.
 *
 * @param seq
 * @param persistedChanges
 */
PersistResult.draftUpdated = function draftUpdated(
  seq: number,
  persistedChanges: DraftChange[],
) {
  return PersistResult(seq, undefined, undefined, persistedChanges)
}

/**
 * Operation failed.
 *
 * @param seq
 * @param error
 */
PersistResult.failed = function failed(seq: number, error: unknown) {
  return PersistResult(seq, undefined, extractMessageFromError(error))
}

/**
 * Params used to draft a new Job.
 */
interface DraftJobParams {
  teamId: Id | null
  ownerId: Id
  legendVersionId: LegendVersionId
}

/**
 * Result from `parseDraftParams`.
 */
type ParseDraftParamsResult = readonly [
  DraftJobParams | null,
  readonly DraftChange[],
]

// We don't currently have enough info.
const EMPTY_DRAFT_PARAMS: ParseDraftParamsResult = [null, [] as const] as const

/**
 * Gets draft params from changes.
 * @param changes
 * @returns A tuple of the draft creation params and the remaining
 *  changes that can be used to update afterwards. If the first element
 *  of the tuple is `null`, we don't currently have enough info to create
 *  a draft, which will cause the Persister to include those changes in the
 *  next attempt once more info has been collected.
 */
function parseDraftParams(
  changes: readonly DraftChange[],
): ParseDraftParamsResult {
  let ownerId: Id | undefined
  let legendVersionId: LegendVersionId | undefined
  let teamId: Id | null

  const postCreationChanges: DraftChange[] = []
  for (const change of changes) {
    switch (change.type) {
      // We don't actually use this one.
      case 'WorkspaceSelected':
        break
      case 'TeamSelected':
        teamId = change.team === TeamSelection.myself ? null : change.team?.id
        break
      case 'LegendSelected':
        legendVersionId = change.legend?.versionId
        break
      case 'OwnerSelected':
        ownerId = change.owner?.user.publicId
        break
      default:
        // Other changes that we want to persist
        // after having created the job.
        postCreationChanges.push(change)
    }
  }

  if (
    legendVersionId === undefined ||
    teamId === undefined ||
    ownerId === undefined
  ) {
    return EMPTY_DRAFT_PARAMS
  }

  const params: DraftJobParams = {
    teamId,
    ownerId,
    legendVersionId,
  }

  return [params, postCreationChanges]
}

/**
 * Factory for creating a {@link DraftPersister}.
 */
export type DraftPersisterFactory = ReturnType<
  typeof createDraftPersisterFactory
>

/** @see DraftPersisterFactory */
export function createDraftPersisterFactory(
  jobStore: JobStore,
  draftJobStore: DraftJobStore,
  changeDefaultBillingMethod: ChangeDefaultBillingMethodFn,
) {
  return (changes$: Observable<DraftChange>) =>
    new DraftPersister(
      changes$,
      jobStore,
      draftJobStore,
      changeDefaultBillingMethod,
    )
}
